import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { IStatsAggregates } from '../../models/trainingStatistics';

/**
 * Export PDF editoriale statistiche allenamento (spec v1.1.1 sez 21 riga 2948).
 * Usa jsPDF + autoTable per layout professionale A4 con logo e intestazioni.
 */
export function exportStatsAsPdf(
  aggregates: IStatsAggregates,
  teamName: string,
  periodLabel: string,
): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const marginX = 14;
  let y = 18;

  // ─── Header ─────────────────────────────────────────────────────────────
  doc.setFillColor(6, 52, 68);
  doc.rect(0, 0, 210, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('LEOPART SportTech', marginX, 6.5);
  doc.text('Statistiche Allenamento', 210 - marginX, 6.5, { align: 'right' });

  doc.setTextColor(0, 33, 44);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(teamName, marginX, y + 4);
  y += 8;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`Periodo: ${periodLabel}`, marginX, y + 4);
  y += 12;

  // ─── KPI Overview ───────────────────────────────────────────────────────
  doc.setTextColor(6, 52, 68);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Panoramica periodo', marginX, y);
  y += 2;
  doc.setDrawColor(230, 230, 230);
  doc.line(marginX, y, 210 - marginX, y);
  y += 5;

  const kpiRows = [
    ['Sessioni totali', aggregates.totalSessions],
    ['Minuti totali', aggregates.totalMinutes],
    ['Blocchi totali', aggregates.totalBlocks],
  ];
  autoTable(doc, {
    startY: y,
    head: [['Metrica', 'Valore']],
    body: kpiRows as any,
    theme: 'striped',
    headStyles: { fillColor: [6, 52, 68], textColor: 255, fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    margin: { left: marginX, right: marginX },
  });
  y = (doc as any).lastAutoTable.finalY + 8;

  // ─── Distribuzione per fase seduta ──────────────────────────────────────
  if (aggregates.bySessionPhase && aggregates.bySessionPhase.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(6, 52, 68);
    doc.text('Distribuzione per fase seduta', marginX, y);
    y += 2;
    doc.line(marginX, y, 210 - marginX, y);
    y += 5;
    autoTable(doc, {
      startY: y,
      head: [['Fase', 'Minuti', '%']],
      body: aggregates.bySessionPhase.map((p: any) => [
        p.phaseId,
        p.minutes,
        (p.percentage ?? 0).toFixed(1) + '%',
      ]),
      theme: 'striped',
      headStyles: { fillColor: [6, 52, 68], textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      margin: { left: marginX, right: marginX },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // ─── Metodi didattici ──────────────────────────────────────────────────
  if (aggregates.byDidacticMethod && aggregates.byDidacticMethod.length > 0) {
    if (y > 240) {
      doc.addPage();
      y = 18;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(6, 52, 68);
    doc.text('Distribuzione per metodo didattico', marginX, y);
    y += 2;
    doc.line(marginX, y, 210 - marginX, y);
    y += 5;
    autoTable(doc, {
      startY: y,
      head: [['Metodo', 'Minuti', '%']],
      body: aggregates.byDidacticMethod.map((m: any) => [
        m.methodId,
        m.minutes,
        (m.percentage ?? 0).toFixed(1) + '%',
      ]),
      theme: 'striped',
      headStyles: { fillColor: [6, 52, 68], textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      margin: { left: marginX, right: marginX },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // ─── Top 10 tecnici ────────────────────────────────────────────────────
  if (
    aggregates.byTechnicalObjective &&
    aggregates.byTechnicalObjective.length > 0
  ) {
    if (y > 230) {
      doc.addPage();
      y = 18;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(6, 52, 68);
    doc.text('Top 10 obiettivi tecnici', marginX, y);
    y += 2;
    doc.line(marginX, y, 210 - marginX, y);
    y += 5;
    autoTable(doc, {
      startY: y,
      head: [['Obiettivo', 'Minuti', '%']],
      body: aggregates.byTechnicalObjective
        .slice(0, 10)
        .map((o: any) => [
          o.objectiveId,
          o.minutes,
          (o.percentage ?? 0).toFixed(1) + '%',
        ]),
      theme: 'striped',
      headStyles: { fillColor: [6, 52, 68], textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      margin: { left: marginX, right: marginX },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // ─── Top 10 tattici ────────────────────────────────────────────────────
  if (
    aggregates.byTacticalObjective &&
    aggregates.byTacticalObjective.length > 0
  ) {
    if (y > 230) {
      doc.addPage();
      y = 18;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(6, 52, 68);
    doc.text('Top 10 obiettivi tattici', marginX, y);
    y += 2;
    doc.line(marginX, y, 210 - marginX, y);
    y += 5;
    autoTable(doc, {
      startY: y,
      head: [['Obiettivo', 'Minuti', '%']],
      body: aggregates.byTacticalObjective
        .slice(0, 10)
        .map((o: any) => [
          o.objectiveId,
          o.minutes,
          (o.percentage ?? 0).toFixed(1) + '%',
        ]),
      theme: 'striped',
      headStyles: { fillColor: [6, 52, 68], textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      margin: { left: marginX, right: marginX },
    });
  }

  // Footer
  const pageCount = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Pagina ${i}/${pageCount} · Generato da LEOPART · ${new Date().toLocaleDateString('it-IT')}`,
      105,
      290,
      { align: 'center' },
    );
  }

  doc.save(
    `training-stats-${teamName.replace(/\s+/g, '-').toLowerCase()}-${new Date()
      .toISOString()
      .slice(0, 10)}.pdf`,
  );
}
